import { pathnameWithoutLocalePrefix } from '@susu/locale-utils'

export type PageType =
  | 'account'
  | 'black_tie_configurator'
  | 'cart'
  | 'category_landing'
  | 'category'
  | 'checkout'
  | 'custommade'
  | 'error'
  | 'giftcard'
  | 'home'
  | 'journal'
  | 'login'
  | 'lookbuilder'
  | 'product'
  | 'return'
  | 'store'
  | 'support'
  | 'wishlist'
  | 'booking_tool'

export const getPageType = (): PageType => {
  // Guard statement for SSR/backend/test environments where window is not defined
  if (typeof window === 'undefined') {
    return 'home'
  }

  const pathname = pathnameWithoutLocalePrefix(window.location?.pathname || '')
  const hostname = window.location.hostname

  // Appointment booking pages (subdomain)
  if (hostname.includes('appointment.suitsupply.com')) {
    return 'booking_tool'
  }

  // Error pages
  if (
    pathname.startsWith('/error') ||
    pathname.startsWith('/404') ||
    pathname.startsWith('/500')
  ) {
    return 'error'
  }

  // Support pages
  if (
    pathname.startsWith('/support') ||
    pathname.startsWith('/help') ||
    pathname.startsWith('/contact')
  ) {
    return 'support'
  }

  // Account and login pages
  if (
    pathname.startsWith('/account') ||
    pathname.startsWith('/profile') ||
    pathname.startsWith('/my-account')
  ) {
    return 'account'
  }

  if (
    pathname.startsWith('/login') ||
    pathname.startsWith('/signin') ||
    pathname.startsWith('/register') ||
    pathname.startsWith('/signup')
  ) {
    return 'login'
  }

  // Wishlist
  if (pathname.startsWith('/wishlist') || pathname.startsWith('/favorites')) {
    return 'wishlist'
  }

  // Cart and checkout flow
  if (pathname.startsWith('/cart') || pathname.startsWith('/bag')) {
    return 'cart'
  }

  if (
    pathname.startsWith('/checkout') ||
    pathname.startsWith('/payment') ||
    pathname.startsWith('/billing')
  ) {
    return 'checkout'
  }

  // Gift card pages
  if (pathname.startsWith('/giftcard')) {
    return 'giftcard'
  }

  // Store locator and store pages
  if (
    pathname.startsWith('/stores') ||
    pathname.startsWith('/store/') ||
    pathname.startsWith('/find-store')
  ) {
    return 'store'
  }

  // Journal and content pages
  if (
    pathname.startsWith('/journal') ||
    pathname.startsWith('/blog') ||
    pathname.startsWith('/content')
  ) {
    return 'journal'
  }

  // Custom made configurator
  if (
    pathname.startsWith('/men/black-tie-package') ||
    pathname.startsWith('/black-tie-package')
  ) {
    return 'black_tie_configurator'
  }

  // Custom made pages
  if (
    pathname.startsWith('/custom-made') ||
    pathname.startsWith('/custommade') ||
    pathname.startsWith('/configurator')
  ) {
    return 'custommade'
  }

  // Look builder
  if (
    pathname.startsWith('/lookbuilder') ||
    pathname.startsWith('/look-builder') ||
    pathname.startsWith('/outfit-builder')
  ) {
    return 'lookbuilder'
  }

  // Product pages (individual products with SKU pattern)
  if (
    pathname.match(/\/[A-Z0-9]+\.html$/) || // SKU pattern like /H7404.html
    pathname.match(/\/[A-Z0-9]+-[A-Z0-9]+\.html$/) || // SKU with variant like /C7334-S.html
    pathname.match(/\/[a-z0-9-]+\/[A-Z0-9]+\.html$/) // Product path with SKU
  ) {
    return 'product'
  }

  // Category landing pages (special collection landing pages)
  if (
    pathname.startsWith('/discover-all-suits') ||
    pathname.startsWith('/shop-tuxedo-collection')
  ) {
    return 'category_landing'
  }

  // Category pages (including special collections and product category listings)
  if (
    pathname.startsWith('/new-arrivals') ||
    pathname.startsWith('/best-sellers') ||
    pathname.startsWith('/sale') ||
    pathname.startsWith('/clearance') ||
    pathname.startsWith('/men/') ||
    pathname.startsWith('/women/') ||
    pathname.startsWith('/accessories/') ||
    pathname.startsWith('/suits/') ||
    pathname.startsWith('/shirts/') ||
    pathname.startsWith('/trousers/') ||
    pathname.startsWith('/shoes/') ||
    pathname.startsWith('/coats/') ||
    pathname.startsWith('/jackets/') ||
    pathname.startsWith('/knitwear/') ||
    pathname.startsWith('/ties/') ||
    pathname.startsWith('/belts/') ||
    pathname.startsWith('/socks/') ||
    pathname.startsWith('/handkerchiefs/') ||
    pathname.startsWith('/cufflinks/')
  ) {
    return 'category'
  }

  // Returns page
  if (pathname.startsWith('/returns')) {
    return 'return'
  }

  // Home page
  if (pathname === '/' || pathname === '') {
    return 'home'
  }

  // Default fallback
  return 'home'
}
